import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
	compose,
	withHandlers,
	withProps,
	branch,
	renderComponent,
} from 'recompose';
import { get, noop } from 'lodash';
import classnames from 'classnames';
import {
	Experiment,
	ExperimentTreatment,
} from '@sparefoot/react-core/experiments';

import { getRoundedRating } from 'utils/rating';

import { withShowHide, withSparefootSelfStorageSchema } from 'decorators';
import { withOnVisibleFacilityHandler } from 'decorators/facility';

import { Link } from 'components/core/Link';
import { Button } from 'components/core/Button';
import { Text } from 'components/core/Text';
import { UnitList, LinkedUnitDetails } from 'components/unit/UnitList';
import { TruckPromo } from 'components/partner/TruckPromo';
import { ViewportAction } from 'components/core/ViewportAction';
import { OONFacilityCard } from 'components/search/OONFacilityCard';
import OmiBanner from 'components/search/OMI/OmiBanner';
import { setLocation } from 'lib/helpers';

import FacilityCardHeader from './FacilityCardHeader';
import VipFacilityCardHeader from './VipFacilityCardHeader';

import './facilityCard.scss';
import HiddenFacilityCard from './HiddenFacilityCard';
import { trackCTAClick } from '../../core/library/tracking-utils/tracking/clickTrackingUtils';
import { withExperiment } from '@sparefoot/react-core/decorators';
import { ClickToShowNumberButton } from 'components/contact/ClickToShowNumberButton';

export const enhance = compose(
	branch(
		({ facility }) => facility.isHidden,
		renderComponent(HiddenFacilityCard)
	),
	branch(
		({ facility }) => !facility.inNetwork,
		renderComponent(OONFacilityCard)
	),
	branch(
		({ facility }) => facility.isHidden,
		renderComponent(HiddenFacilityCard)
	),
	branch(
		({ facility }) => !facility.inNetwork,
		renderComponent(OONFacilityCard)
	),
	withExperiment('sf_15309_giftcard_incentives'), // sf_15309_giftcard_incentives
	withProps(({ facility, searchType, searchLocation, cityDrilldown }) => ({
		location: facility.location, // city state zip
		bestReview: facility.bestReview,
		color: facility.color,
		tagLine:
			!!facility.isFullServiceStandalone && facility.tagLine
				? facility.tagLine
				: null,
		distance: `${facility.distance} miles away`,
		name: facility.name,
		average:
			facility.reviewMetadata &&
			getRoundedRating(facility.reviewMetadata.average),
		reviewCount: facility.reviewMetadata && facility.reviewMetadata.count,
		hasReviews:
			facility.reviewMetadata && facility.reviewMetadata.count > 0,
		isPenskeRental:
			searchType === 'trucks' && facility.messaging.penskeRental,
		isTruckRental:
			searchType === 'trucks' && facility.messaging.truckRental,
		isFreeTruckRental:
			searchType === 'trucks' && facility.messaging.freeTruckRental,
		facilityImage: facility.images[0],
		units: facility.units,
		city: get(searchLocation, 'city'),
		state: get(searchLocation, 'state'),
		facilityZipCode: get(facility, 'location.zip'), //
		facilityUrl: facility.url.facility,
		noFollowFacilityLink: searchType === 'vehicle',
	})),
	withOnVisibleFacilityHandler,
	withHandlers({
		handleMapAndHoursClick: (props) => (event) => {
			event.preventDefault();
			props.openModal(props.facility.id);
		},
		handleMouseEnter: (props) => () => {
			if (props.onMouseEnter) {
				props.onMouseEnter(props.facility.id);
			}
		},
		handleMouseLeave: (props) => () => {
			if (props.onMouseLeave) {
				props.onMouseLeave(props.facility.id);
			}
		},
		handleViewAllPricesRedirect: (props) => () => {
			trackCTAClick(
				'view-all-units',
				'View All units Button',
				'View All units',
				props.facilityUrl
			);

			setLocation(props.facilityUrl);
		},
		fetchSearchCityPhoneNumber:
			({ getLocalPhoneNumberByCityState, city, state }) =>
			() =>
				getLocalPhoneNumberByCityState(city, state),
	}),
	withShowHide('modal'),
	withSparefootSelfStorageSchema
);

/* eslint-disable max-len */
export function FacilityCard({
	facility,
	bestReview,
	handleViewAllPricesRedirect,
	handleMapAndHoursClick,
	location,
	tagLine,
	distance,
	page,
	name,
	average,
	reviewCount,
	hasReviews,
	isPenskeRental,
	isTruckRental,
	isFreeTruckRental,
	facilityImage,
	units,
	facilityUrl,
	handleMouseEnter,
	handleMouseLeave,
	position,
	noFollowFacilityLink,
	fetchSearchCityPhoneNumber,
	onVisible,
	className,
	percentVisible,
	isVipFacility,
	vipAccount,
	vipFacilityImages,
	vipShowMapHandler,
	lazyLoad,
	imageBadge,
	withSchema,
	selfStorageSchema,
	fullWidthCard,
	sf15309Variation,
}) {
	return (
		<ViewportAction
			onVisible={onVisible}
			percentVisible={percentVisible}
			className={className}
		>
			<div
				className={classnames('facility-card', {
					fullWidthCard,
					paid: page,
				})}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<div
					className={classnames('facility-content', {
						vip: isVipFacility,
					})}
				>
					{isVipFacility ? (
						<VipFacilityCardHeader
							average={average}
							distance={distance}
							facilityUrl={facilityUrl}
							facilityImage={facilityImage}
							hasReviews={hasReviews}
							name={name}
							reviewCount={reviewCount}
							tagLine={tagLine}
							streetAddress={location.streetAddress}
							city={location.city}
							state={location.state}
							zip={location.zip}
							handleShowMap={vipShowMapHandler}
							noFollowFacilityLink={noFollowFacilityLink}
							fetchSearchCityPhoneNumber={
								fetchSearchCityPhoneNumber
							}
							isVipFacility={isVipFacility}
							vipAccount={vipAccount}
							vipFacilityImages={vipFacilityImages}
							staticMapUrl={facility.staticMapUrl}
							facilityBadges={
								facility.badges.filter(
									(badge) =>
										!(
											badge === 'giftcard' &&
											sf15309Variation !== 'variation'
										)
								) // sf_15309_giftcard_incentives
							}
							giftCardValue={
								facility.giftCardOffer?.giftCardValue
							}
						/>
					) : (
						<FacilityCardHeader
							average={average}
							distance={distance}
							facilityUrl={facilityUrl}
							facilityImage={facilityImage}
							hasReviews={hasReviews}
							name={name}
							reviewCount={reviewCount}
							tagLine={tagLine}
							streetAddress={location.streetAddress}
							city={location.city}
							state={location.state}
							page={page}
							zip={location.zip}
							handleMapAndHoursClick={handleMapAndHoursClick}
							noFollowFacilityLink={noFollowFacilityLink}
							fetchSearchCityPhoneNumber={
								fetchSearchCityPhoneNumber
							}
							lazyLoad={lazyLoad}
							imageBadge={imageBadge}
							facilityBadges={facility.badges.filter(
								(badge) =>
									!(
										badge === 'giftcard' &&
										sf15309Variation !== 'variation'
									)
							)} // sf_15309_giftcard_incentives
							giftCardValue={
								facility.giftCardOffer?.giftCardValue
							}
						/>
					)}
					{!!bestReview && (
						<div
							className={classnames('best-review', {
								'vip-best-review': isVipFacility,
							})}
						>
							<Fragment>
								<span className="review-text">
									&ldquo;{bestReview.message}
								</span>
								&rdquo;{' '}
								<Link
									href={`${facilityUrl}#review-${bestReview.id}`}
									rel={noFollowFacilityLink ? 'nofollow' : ''}
								>
									Full Review
								</Link>
							</Fragment>
						</div>
					)}

					{!!facility.hasOnlineMoveIns && ( // sf_11349_sfdc_omi
						<Experiment name="sf_11349_sfdc_omi">
							<ExperimentTreatment variation="variation">
								<OmiBanner />
							</ExperimentTreatment>
						</Experiment>
					)}

					{isPenskeRental && <TruckPromo truckType="penskeRental" />}
					{isTruckRental && <TruckPromo truckType="truckRental" />}
					{isFreeTruckRental && (
						<TruckPromo truckType="freeTruckRental" />
					)}
				</div>
				<UnitList
					facility={facility}
					unitDetailsComponent={LinkedUnitDetails}
					units={units}
					facilityPosition={position}
					isVipUnit={isVipFacility}
					showIcons={page === 'paid'}
					className={classnames({
						'badges-spacing':
							isVipFacility && facility.badges.length,
					})}
				/>
				<div className="view-all-units-container">
					<Button
						primary
						pink
						className="view-all-units"
						onClick={handleViewAllPricesRedirect}
						rel={noFollowFacilityLink ? 'nofollow' : ''}
						productClick
					>
						<Text strong>View all units</Text>
					</Button>
					{page === 'paid' && (
						<ClickToShowNumberButton
							showAsLink={false}
							className="phone-number-button"
							outline
							size="standard"
							fetchPhoneNumber={fetchSearchCityPhoneNumber}
							label="Call & Reserve Instantly"
						/>
					)}
				</div>
			</div>
			{withSchema && (
				<script
					key="schema"
					type="application/ld+json"
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{ __html: selfStorageSchema }}
				/>
			)}
		</ViewportAction>
	);
}
/* eslint-enable */

FacilityCard.propTypes = {
	facility: PropTypes.object.isRequired,
	bestReview: PropTypes.object,
	handleMapAndHoursClick: PropTypes.func,
	handleViewAllPricesRedirect: PropTypes.func,
	location: PropTypes.object.isRequired,
	tagLine: PropTypes.string,
	distance: PropTypes.string,
	name: PropTypes.string,
	average: PropTypes.number,
	reviewCount: PropTypes.number,
	hasReviews: PropTypes.bool,
	isPenskeRental: PropTypes.bool,
	isTruckRental: PropTypes.bool,
	isFreeTruckRental: PropTypes.bool,
	facilityImage: PropTypes.string,
	units: PropTypes.arrayOf(PropTypes.object),
	facilityUrl: PropTypes.string,
	handleMouseEnter: PropTypes.func,
	handleMouseLeave: PropTypes.func,
	position: PropTypes.number,
	noFollowFacilityLink: PropTypes.bool,
	fetchSearchCityPhoneNumber: PropTypes.func,
	onVisible: PropTypes.func,
	percentVisible: PropTypes.number.isRequired,
	isVipFacility: PropTypes.bool,
	vipAccount: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
	vipFacilityImages: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string,
			atl: PropTypes.string,
		})
	),
	vipShowMapHandler: PropTypes.func,
	lazyLoad: PropTypes.bool,
	imageBadge: PropTypes.node,
	withSchema: PropTypes.bool,
	selfStorageSchema: PropTypes.string,
	fullWidthCard: PropTypes.bool,
	sf15309Variation: PropTypes.string,
	page: PropTypes.string,
	className: PropTypes.string,
	cityDrilldown: PropTypes.arrayOf(PropTypes.object),
};

FacilityCard.defaultProps = {
	handleMapAndHoursClick: noop,
};

export default enhance(FacilityCard);
